<template>
  <AppDialog :modal-id="modalId" content-class="app-dialog-content--xl">
    <template #default="{ close }">
    <div class="vm-migrate-dialog-surface">
      <div class="modal-header">
        <h3 class="modal-title">Select HyperVisor for VM Migration</h3>
        <button type="button" class="btn-close" aria-label="Close" @click="close"></button>
      </div>

      <div class="modal-body">
        <div class="vm-migrate-modal">
          <div class="vm-migrate-modal-summary">
            <div class="vm-migrate-modal-summary-main">
              <strong>{{ state.vmName }}</strong>
              <span>Current HyperVisor: {{ state.currentHypervisorName }}</span>
            </div>
            <span class="app-status-chip" :class="state.stateClass">{{ state.stateLabel }}</span>
          </div>

          <table class="table table-bordered table-condensed vm-migrate-modal-table">
            <thead>
              <tr>
                <th><div class="text-center">HyperVisor</div></th>
                <th><div class="text-center">CPU Usage</div></th>
                <th><div class="text-center">Memory Usage</div></th>
                <th><div class="text-center">Status</div></th>
                <th><div class="text-center">Migration</div></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in state.rows" :key="`vm-migrate-hv-${row.id}`" :class="{ 'is-maintenance-row': row.reason === 'maintenance' }">
                <th class="vm-migrate-modal-hv-name">
                  <div>{{ row.name }}</div>
                </th>
                <td>
                  <div class="vm-migrate-usage-bar">
                    <div class="vm-migrate-usage-bar-fill" :class="progressClass(row.cpuUsage)" :style="{ width: `${row.cpuUsage}%` }"></div>
                    <span class="vm-migrate-usage-bar-label">{{ row.cpuUsage }}%</span>
                  </div>
                </td>
                <td>
                  <div class="vm-migrate-usage-bar">
                    <div class="vm-migrate-usage-bar-fill" :class="progressClass(row.memUsage)" :style="{ width: `${row.memUsage}%` }"></div>
                    <span class="vm-migrate-usage-bar-label">{{ row.memUsage }}%</span>
                  </div>
                </td>
                <td class="text-center">
                  <span class="app-status-chip" :class="row.statusClass">{{ row.statusLabel }}</span>
                </td>
                <td v-if="row.reason === 'here'" class="infomation">Here.</td>
                <td v-else-if="row.reason === 'maintenance'" class="maintenance">Maintenance mode.</td>
                <td v-else-if="row.reason === 'not_migratable'" class="unknown">NOT MIGRATABLE</td>
                <td v-else-if="row.reason === 'not_your_vm'" class="unknown">NOT YOUR VM</td>
                <td v-else class="vm-migrate-modal-action">
                  <div class="text-center">
                      <button type="button" class="btn btn-primary btn-sm" title="Migrate" @click.prevent.stop="handleMigrate(row)">
                        Migrate
                      </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </template>
  </AppDialog>
</template>

<script setup>
import { onBeforeUnmount, onMounted, reactive } from "vue"
import AppDialog from "./AppDialog.vue"
import { sccHideModal } from "../composables/bridges/app_core"

const props = defineProps({
  modalId: {
    type: String,
    required: true
  },
  vmId: {
    type: [String, Number],
    required: true
  },
  vmName: {
    type: String,
    default: "VM"
  },
  currentHypervisorName: {
    type: String,
    default: "N/A"
  },
  stateLabel: {
    type: String,
    default: "UNKNOWN"
  },
  stateClass: {
    type: String,
    default: "is-unknown"
  },
  rows: {
    type: Array,
    default: () => []
  }
})

const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
const state = reactive({
  vmId: props.vmId,
  vmName: props.vmName,
  currentHypervisorName: props.currentHypervisorName,
  stateLabel: props.stateLabel,
  stateClass: props.stateClass,
  rows: Array.isArray(props.rows) ? props.rows : []
})

function markCloneProgressFailed(message) {
  window.sccStatusShell?.markCloneProgressFailed?.(message)
}

function sccBeginMigrationPolling(statusUrl) {
  window.sccStatusShell?.sccBeginMigrationPolling?.(statusUrl)
}

function sccShowMigrationOverlay(vmName, fromHv, toHv) {
  window.sccStatusShell?.sccShowMigrationOverlay?.(vmName, fromHv, toHv)
}

function sccShowToast(level, message) {
  window.sccStatusShell?.sccShowToast?.(level, message)
}

function progressClass(value) {
  if (value <= 60) return "is-info"
  if (value <= 80) return "is-warning"
  return "is-danger"
}

function hideModal() {
  const modal = document.getElementById(props.modalId)
  if (!modal) {
    return
  }

  sccHideModal(modal)
}

function handleModalUpdate(event) {
  const detail = event.detail || {}

  state.vmId = detail.vmId ?? state.vmId
  state.vmName = detail.vmName ?? state.vmName
  state.currentHypervisorName = detail.currentHypervisorName ?? state.currentHypervisorName
  state.stateLabel = detail.stateLabel ?? state.stateLabel
  state.stateClass = detail.stateClass ?? state.stateClass
  state.rows = Array.isArray(detail.rows) ? detail.rows : state.rows
}

function showMigrationOverlay(row) {
  sccShowMigrationOverlay(
    row.migrateVmName || state.vmName || "VM",
    row.migrateFromHv || state.currentHypervisorName || "Source",
    row.migrateToHv || row.name || "Destination"
  )
}

function beginMigrationPolling(statusUrl) {
  if (!statusUrl) {
    markCloneProgressFailed("Migration progress URL was not returned.")
    return
  }
  sccBeginMigrationPolling(statusUrl)
}

async function handleMigrate(row) {
  const formBody = new URLSearchParams()
  formBody.set("dest", row.id)

  try {
    const response = await fetch(row.migrateUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
        "X-Requested-With": "XMLHttpRequest",
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {})
      },
      credentials: "same-origin",
      body: formBody.toString()
    })

    const text = await response.text()
    let payload = null
    if (text) {
      try {
        payload = JSON.parse(text)
      } catch {
        payload = null
      }
    }

    if (!response.ok) {
      throw new Error((payload && payload.message) || text || "Failed to start migration.")
    }

    hideModal()
    showMigrationOverlay(row)
    beginMigrationPolling(payload?.status_url)
  } catch (error) {
    markCloneProgressFailed(error.message || "Failed to start migration.")
    sccShowToast("danger", error.message || "Failed to start migration.")
  }
}

onMounted(() => {
  const modal = document.getElementById(props.modalId)

  if (!modal) {
    return
  }

  modal.addEventListener('scc:vm-migrate-modal:update', handleModalUpdate)
})

onBeforeUnmount(() => {
  const modal = document.getElementById(props.modalId)

  if (!modal) {
    return
  }

  modal.removeEventListener('scc:vm-migrate-modal:update', handleModalUpdate)
})
</script>
